<script setup>
import { onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import InboxReconnectionRequired from '../../components/InboxReconnectionRequired.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const LOGIN_CANCELLED = 'login_cancelled';

// Meta posts the business, WABA and number that the admin selected in the popup.
let selectedBusiness = null;

const handleSignupMessage = event => {
  if (!event.origin.endsWith('facebook.com')) return;

  try {
    const data =
      typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (data?.type === 'WA_EMBEDDED_SIGNUP' && data.event === 'FINISH') {
      selectedBusiness = data.data;
    }
  } catch {
    // Ignore messages that are not from the embedded signup
  }
};

const stopListening = () => {
  window.removeEventListener('message', handleSignupMessage);
};

const loadFacebookSdk = async () => {
  await loadScript('https://connect.facebook.net/en_US/sdk.js', {
    async: true,
    defer: true,
    crossOrigin: 'anonymous',
  });
  window.FB.init({
    appId: window.chatwootConfig?.whatsappAppId,
    autoLogAppEvents: true,
    xfbml: true,
    version: window.chatwootConfig?.whatsappApiVersion || 'v22.0',
  });
};

const loginWithEmbeddedSignup = () =>
  new Promise((resolve, reject) => {
    window.FB.login(
      response => {
        if (response.authResponse?.code) {
          resolve(response.authResponse.code);
        } else {
          reject(new Error(response.error || LOGIN_CANCELLED));
        }
      },
      {
        config_id: window.chatwootConfig?.whatsappConfigurationId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      }
    );
  });

// The backend checks that the signed-in number is the number of this inbox, and then
// reconnects this inbox and clears the reconnect flag.
const reconnect = async code => {
  const config = props.inbox.provider_config || {};
  const wabaId = selectedBusiness?.waba_id || config.business_account_id;
  await store.dispatch('inboxes/createWhatsAppEmbeddedSignup', {
    code,
    business_id: selectedBusiness?.business_id || wabaId,
    waba_id: wabaId,
    phone_number_id:
      selectedBusiness?.phone_number_id || config.phone_number_id,
    inbox_id: props.inbox.id,
  });
};

const requestAuthorization = async () => {
  selectedBusiness = null;
  window.addEventListener('message', handleSignupMessage);
  try {
    await loadFacebookSdk();
    const code = await loginWithEmbeddedSignup();
    await reconnect(code);
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.RECONNECTED'));
  } catch (error) {
    if (error.name === 'ScriptLoaderError') {
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR'));
    } else if (error.message === LOGIN_CANCELLED) {
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    } else {
      useAlert(
        (error.response && parseAPIErrorResponse(error)) ||
          t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
      );
    }
  } finally {
    stopListening();
  }
};

onBeforeUnmount(stopListening);
</script>

<template>
  <InboxReconnectionRequired
    class="mx-8 mt-5"
    :message="t('INBOX_MGMT.RECONNECT.WHATSAPP', { phone: inbox.phone_number })"
    @reauthorize="requestAuthorization"
  />
</template>
